import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../../firebase/config";
import {
  ensureAdminProfile,
  formatDateTime,
  normalizeCategory,
  normalizeContact,
  normalizeOrder,
  normalizeProduct,
  resolveMediaUrl,
} from "../../firebase/helpers";
import Logo from "../images/logo.png";

const initialCategoryForm = {
  id: "",
  categori_name_ar: "",
  categori_photo: "",
  sort_order: 0,
  is_active: true,
};

const initialProductForm = {
  id: "",
  product_title_ar: "",
  product_description_ar: "",
  product_price: "",
  product_code: "",
  categories_id: "",
  in_discount: 0,
  discount_price: "",
  is_active: true,
  images: [],
};

const orderStatuses = [
  { value: "new", label: "جديد" },
  { value: "processing", label: "قيد المعالجة" },
  { value: "completed", label: "مكتمل" },
  { value: "cancelled", label: "ملغي" },
];

const contactStatuses = [
  { value: "new", label: "جديد" },
  { value: "replied", label: "تم الرد" },
  { value: "archived", label: "مؤرشف" },
];

const uploadSingleFile = async (folder, file) => {
  if (!file) return "";
  const cleanName = String(file.name || "file").replace(/\s+/g, "-");
  const storageRef = ref(storage, `${folder}/${Date.now()}-${cleanName}`);
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
};

const uploadMultipleFiles = async (folder, files) => {
  if (!files?.length) return [];
  const uploads = await Promise.all(
    Array.from(files).map((file) => uploadSingleFile(folder, file))
  );
  return uploads.filter(Boolean);
};

function AdminDashboard() {
  const [authState, setAuthState] = useState({
    loading: true,
    user: null,
  });
  const [activeTab, setActiveTab] = useState("overview");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [categoryForm, setCategoryForm] = useState(initialCategoryForm);
  const [productForm, setProductForm] = useState(initialProductForm);
  const [categoryFile, setCategoryFile] = useState(null);
  const [productFiles, setProductFiles] = useState([]);
  const [feedback, setFeedback] = useState({ type: "", text: "" });
  const [saving, setSaving] = useState(false);
  const categoryFormRef = useRef(null);
  const productFormRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await ensureAdminProfile(user);
      }
      setAuthState({ loading: false, user });
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authState.user) return undefined;

    const unsubscribers = [
      onSnapshot(
        query(collection(db, "categories"), orderBy("sort_order", "asc")),
        (snapshot) => setCategories(snapshot.docs.map(normalizeCategory))
      ),
      onSnapshot(
        query(collection(db, "products"), orderBy("createdAt", "desc")),
        (snapshot) => setProducts(snapshot.docs.map(normalizeProduct))
      ),
      onSnapshot(
        query(collection(db, "orders"), orderBy("createdAt", "desc")),
        (snapshot) => setOrders(snapshot.docs.map(normalizeOrder))
      ),
      onSnapshot(
        query(collection(db, "contacts"), orderBy("createdAt", "desc")),
        (snapshot) => setContacts(snapshot.docs.map(normalizeContact))
      ),
    ];

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [authState.user]);

  const stats = useMemo(
    () => [
      {
        title: "عدد التصنيفات",
        value: categories.length,
        icon: "ci-layers",
      },
      {
        title: "عدد المنتجات",
        value: products.length,
        icon: "ci-box",
      },
      {
        title: "الطلبات الجديدة",
        value: orders.filter((item) => item.status === "new").length,
        icon: "ci-basket",
      },
      {
        title: "الرسائل الجديدة",
        value: contacts.filter((item) => item.status === "new").length,
        icon: "ci-mail",
      },
    ],
    [categories, products, orders, contacts]
  );

  if (authState.loading) {
    return (
      <main className="content-wrapper">
        <section className="container py-5 text-center">
          <div className="spinner-border" role="status" />
        </section>
      </main>
    );
  }

  if (!authState.user) {
    return <Navigate to="/admin/login" replace />;
  }

  const resetFeedback = () => {
    setFeedback({ type: "", text: "" });
  };

  const showFeedback = (type, text) => {
    setFeedback({ type, text });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetCategoryForm = () => {
    setCategoryForm(initialCategoryForm);
    setCategoryFile(null);
  };

  const resetProductForm = () => {
    setProductForm(initialProductForm);
    setProductFiles([]);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryForm.categori_name_ar) {
      showFeedback("danger", "اسم التصنيف مطلوب.");
      return;
    }

    try {
      setSaving(true);
      resetFeedback();

      let imageUrl = categoryForm.categori_photo || "";
      if (categoryFile) {
        imageUrl = await uploadSingleFile("categories", categoryFile);
      }

      const payload = {
        categori_name_ar: categoryForm.categori_name_ar,
        categori_photo: imageUrl,
        sort_order: Number(categoryForm.sort_order || 0),
        is_active: Boolean(categoryForm.is_active),
        updatedAt: serverTimestamp(),
      };

      if (categoryForm.id) {
        await updateDoc(doc(db, "categories", categoryForm.id), payload);
        showFeedback("success", "تم تحديث التصنيف بنجاح.");
      } else {
        await addDoc(collection(db, "categories"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        showFeedback("success", "تمت إضافة التصنيف بنجاح.");
      }

      resetCategoryForm();
    } catch (error) {
      showFeedback("danger", "تعذر حفظ التصنيف.");
    } finally {
      setSaving(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();

    if (!productForm.product_title_ar || !productForm.product_price || !productForm.categories_id) {
      showFeedback("danger", "اسم المنتج والسعر والتصنيف مطلوبة.");
      return;
    }

    try {
      setSaving(true);
      resetFeedback();

      let images = Array.isArray(productForm.images) ? productForm.images : [];
      if (productFiles?.length) {
        const uploadedUrls = await uploadMultipleFiles("products", productFiles);
        images = uploadedUrls.map((url) => ({ product_photo: url }));
      }

      const payload = {
        product_title_ar: productForm.product_title_ar,
        product_description_ar: productForm.product_description_ar,
        product_price: Number(productForm.product_price || 0),
        product_code: productForm.product_code || `PR-${Date.now()}`,
        categories_id: productForm.categories_id,
        in_discount: Number(productForm.in_discount || 0),
        discount_price: Number(productForm.discount_price || 0),
        images,
        is_active: Boolean(productForm.is_active),
        updatedAt: serverTimestamp(),
      };

      if (productForm.id) {
        await updateDoc(doc(db, "products", productForm.id), payload);
        showFeedback("success", "تم تحديث المنتج بنجاح.");
      } else {
        await addDoc(collection(db, "products"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        showFeedback("success", "تمت إضافة المنتج بنجاح.");
      }

      resetProductForm();
    } catch (error) {
      showFeedback("danger", "تعذر حفظ المنتج.");
    } finally {
      setSaving(false);
    }
  };

  const editCategory = (item) => {
    setActiveTab("categories");
    setCategoryForm({
      id: item.id,
      categori_name_ar: item.categori_name_ar,
      categori_photo: item.categori_photo,
      sort_order: item.sort_order || 0,
      is_active: item.is_active !== false,
    });
    categoryFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const editProduct = (item) => {
    setActiveTab("products");
    setProductForm({
      id: item.id,
      product_title_ar: item.product_title_ar,
      product_description_ar: item.product_description_ar,
      product_price: item.product_price,
      product_code: item.product_code,
      categories_id: item.categories_id,
      in_discount: item.in_discount || 0,
      discount_price: item.discount_price || "",
      is_active: item.is_active !== false,
      images: Array.isArray(item.images) ? item.images : [],
    });
    productFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const deleteCategoryItem = async (id) => {
    const okay = window.confirm("هل تريد حذف هذا التصنيف؟");
    if (!okay) return;

    try {
      await deleteDoc(doc(db, "categories", id));
      showFeedback("success", "تم حذف التصنيف.");
    } catch (error) {
      showFeedback("danger", "تعذر حذف التصنيف.");
    }
  };

  const deleteProductItem = async (id) => {
    const okay = window.confirm("هل تريد حذف هذا المنتج؟");
    if (!okay) return;

    try {
      await deleteDoc(doc(db, "products", id));
      showFeedback("success", "تم حذف المنتج.");
    } catch (error) {
      showFeedback("danger", "تعذر حذف المنتج.");
    }
  };

  const updateOrderStatus = async (id, status) => {
    try {
      await updateDoc(doc(db, "orders", id), {
        status,
        updatedAt: serverTimestamp(),
      });
      showFeedback("success", "تم تحديث حالة الطلب.");
    } catch (error) {
      showFeedback("danger", "تعذر تحديث حالة الطلب.");
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await updateDoc(doc(db, "contacts", id), {
        status,
        updatedAt: serverTimestamp(),
      });
      showFeedback("success", "تم تحديث حالة الرسالة.");
    } catch (error) {
      showFeedback("danger", "تعذر تحديث حالة الرسالة.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  const activeCategoryName = (categoryId) => {
    const match = categories.find((item) => item.id === categoryId);
    return match?.categori_name_ar || "—";
  };

  return (
    <main className="content-wrapper admin-dashboard-page">
      <section className="container py-4 py-lg-5" dir="rtl">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
          <div className="d-flex align-items-center gap-3">
            <img src={Logo} alt="Logo" className="admin-logo" />
            <div>
              <h1 className="h3 mb-1">لوحة إدارة المتجر</h1>
              <p className="text-body-secondary mb-0">
                إدارة كاملة للمنتجات والتصنيفات والطلبات ورسائل العملاء
              </p>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <Link to="/" className="btn btn-outline-secondary rounded-pill">
              المتجر
            </Link>
            <button type="button" onClick={handleLogout} className="btn btn-dark rounded-pill">
              تسجيل الخروج
            </button>
          </div>
        </div>

        {feedback.text ? (
          <div className={`alert alert-${feedback.type}`} role="alert">
            {feedback.text}
          </div>
        ) : null}

        <div className="row g-3 mb-4">
          {stats.map((item) => (
            <div key={item.title} className="col-12 col-sm-6 col-xl-3">
              <div className="card border-0 shadow-sm rounded-5 h-100">
                <div className="card-body d-flex align-items-center gap-3">
                  <div className="admin-stat-icon">
                    <i className={item.icon} />
                  </div>
                  <div>
                    <div className="text-body-secondary small">{item.title}</div>
                    <div className="h3 mb-0">{item.value}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card border-0 shadow-sm rounded-5 overflow-hidden">
          <div className="card-body p-3 p-lg-4">
            <div className="nav nav-pills gap-2 admin-tabs mb-4">
              <button
                type="button"
                className={`btn rounded-pill ${activeTab === "overview" ? "btn-dark" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("overview")}
              >
                نظرة عامة
              </button>
              <button
                type="button"
                className={`btn rounded-pill ${activeTab === "categories" ? "btn-dark" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("categories")}
              >
                التصنيفات
              </button>
              <button
                type="button"
                className={`btn rounded-pill ${activeTab === "products" ? "btn-dark" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("products")}
              >
                المنتجات
              </button>
              <button
                type="button"
                className={`btn rounded-pill ${activeTab === "orders" ? "btn-dark" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("orders")}
              >
                الطلبات
              </button>
              <button
                type="button"
                className={`btn rounded-pill ${activeTab === "contacts" ? "btn-dark" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("contacts")}
              >
                الرسائل
              </button>
            </div>

            {activeTab === "overview" ? (
              <div className="row g-4">
                <div className="col-lg-6">
                  <div className="card border rounded-5 h-100">
                    <div className="card-body">
                      <h2 className="h5 mb-3">أحدث الطلبات</h2>
                      <div className="table-responsive">
                        <table className="table align-middle mb-0">
                          <thead>
                            <tr>
                              <th>الكود</th>
                              <th>العميل</th>
                              <th>الإجمالي</th>
                              <th>الحالة</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.slice(0, 5).map((item) => (
                              <tr key={item.id}>
                                <td>{item.order_code}</td>
                                <td>{item.customer_name || "—"}</td>
                                <td>{item.total_price} د.ع</td>
                                <td>{orderStatuses.find((row) => row.value === item.status)?.label || item.status}</td>
                              </tr>
                            ))}
                            {!orders.length ? (
                              <tr>
                                <td colSpan={4} className="text-center text-body-secondary">
                                  لا توجد طلبات بعد
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="card border rounded-5 h-100">
                    <div className="card-body">
                      <h2 className="h5 mb-3">أحدث الرسائل</h2>
                      <div className="vstack gap-3">
                        {contacts.slice(0, 5).map((item) => (
                          <div key={item.id} className="border rounded-4 p-3">
                            <div className="d-flex align-items-center justify-content-between gap-2">
                              <strong>{item.name || "بدون اسم"}</strong>
                              <span className="badge text-bg-secondary">{item.subject || "بدون موضوع"}</span>
                            </div>
                            <div className="text-body-secondary small mt-2">{item.email || "—"}</div>
                            <p className="mb-0 mt-2">{item.message?.slice(0, 110) || "—"}</p>
                          </div>
                        ))}
                        {!contacts.length ? (
                          <div className="text-center text-body-secondary py-4">لا توجد رسائل بعد</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === "categories" ? (
              <div className="row g-4">
                <div className="col-lg-5" ref={categoryFormRef}>
                  <div className="card border rounded-5">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h2 className="h5 mb-0">
                          {categoryForm.id ? "تعديل التصنيف" : "إضافة تصنيف"}
                        </h2>
                        {categoryForm.id ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary rounded-pill"
                            onClick={resetCategoryForm}
                          >
                            جديد
                          </button>
                        ) : null}
                      </div>

                      <form onSubmit={handleCategorySubmit}>
                        <div className="mb-3">
                          <label className="form-label">اسم التصنيف</label>
                          <input
                            type="text"
                            className="form-control rounded-pill"
                            value={categoryForm.categori_name_ar}
                            onChange={(e) =>
                              setCategoryForm((prev) => ({
                                ...prev,
                                categori_name_ar: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">رابط الصورة</label>
                          <input
                            type="text"
                            className="form-control rounded-pill"
                            value={categoryForm.categori_photo}
                            onChange={(e) =>
                              setCategoryForm((prev) => ({
                                ...prev,
                                categori_photo: e.target.value,
                              }))
                            }
                            placeholder="https://..."
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">أو اختر صورة من الجهاز</label>
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={(e) => setCategoryFile(e.target.files?.[0] || null)}
                          />
                        </div>

                        {categoryForm.categori_photo ? (
                          <div className="mb-3">
                            <img
                              src={resolveMediaUrl(categoryForm.categori_photo, Logo)}
                              alt="category preview"
                              className="admin-preview-image rounded-4"
                            />
                          </div>
                        ) : null}

                        <div className="mb-3">
                          <label className="form-label">الترتيب</label>
                          <input
                            type="number"
                            className="form-control rounded-pill"
                            value={categoryForm.sort_order}
                            onChange={(e) =>
                              setCategoryForm((prev) => ({
                                ...prev,
                                sort_order: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="form-check form-switch mb-4">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={categoryForm.is_active}
                            onChange={(e) =>
                              setCategoryForm((prev) => ({
                                ...prev,
                                is_active: e.target.checked,
                              }))
                            }
                            id="categoryActive"
                          />
                          <label className="form-check-label" htmlFor="categoryActive">
                            نشط
                          </label>
                        </div>

                        <button className="btn btn-dark rounded-pill w-100" type="submit" disabled={saving}>
                          {saving ? "جارٍ الحفظ..." : categoryForm.id ? "تحديث التصنيف" : "إضافة التصنيف"}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>

                <div className="col-lg-7">
                  <div className="table-responsive">
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th>الصورة</th>
                          <th>الاسم</th>
                          <th>الترتيب</th>
                          <th>الحالة</th>
                          <th>الإجراء</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <img
                                src={resolveMediaUrl(item.categori_photo, Logo)}
                                alt={item.categori_name_ar}
                                className="admin-table-thumb rounded-4"
                              />
                            </td>
                            <td>{item.categori_name_ar}</td>
                            <td>{item.sort_order || 0}</td>
                            <td>{item.is_active ? "نشط" : "مخفي"}</td>
                            <td>
                              <div className="d-flex gap-2">
                                <button className="btn btn-sm btn-outline-dark rounded-pill" onClick={() => editCategory(item)}>
                                  تعديل
                                </button>
                                <button className="btn btn-sm btn-outline-danger rounded-pill" onClick={() => deleteCategoryItem(item.id)}>
                                  حذف
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {!categories.length ? (
                          <tr>
                            <td colSpan={5} className="text-center text-body-secondary">
                              لا توجد تصنيفات
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === "products" ? (
              <div className="row g-4">
                <div className="col-lg-5" ref={productFormRef}>
                  <div className="card border rounded-5">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h2 className="h5 mb-0">
                          {productForm.id ? "تعديل المنتج" : "إضافة منتج"}
                        </h2>
                        {productForm.id ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary rounded-pill"
                            onClick={resetProductForm}
                          >
                            جديد
                          </button>
                        ) : null}
                      </div>

                      <form onSubmit={handleProductSubmit}>
                        <div className="mb-3">
                          <label className="form-label">اسم المنتج</label>
                          <input
                            type="text"
                            className="form-control rounded-pill"
                            value={productForm.product_title_ar}
                            onChange={(e) =>
                              setProductForm((prev) => ({
                                ...prev,
                                product_title_ar: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">كود المنتج</label>
                          <input
                            type="text"
                            className="form-control rounded-pill"
                            value={productForm.product_code}
                            onChange={(e) =>
                              setProductForm((prev) => ({
                                ...prev,
                                product_code: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">السعر</label>
                          <input
                            type="number"
                            className="form-control rounded-pill"
                            value={productForm.product_price}
                            onChange={(e) =>
                              setProductForm((prev) => ({
                                ...prev,
                                product_price: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">التصنيف</label>
                          <select
                            className="form-select rounded-pill"
                            value={productForm.categories_id}
                            onChange={(e) =>
                              setProductForm((prev) => ({
                                ...prev,
                                categories_id: e.target.value,
                              }))
                            }
                          >
                            <option value="">اختر التصنيف</option>
                            {categories.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.categori_name_ar}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="mb-3">
                          <label className="form-label">الوصف</label>
                          <textarea
                            className="form-control rounded-4"
                            rows={5}
                            value={productForm.product_description_ar}
                            onChange={(e) =>
                              setProductForm((prev) => ({
                                ...prev,
                                product_description_ar: e.target.value,
                              }))
                            }
                          />
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">هل يوجد خصم؟</label>
                            <select
                              className="form-select rounded-pill"
                              value={productForm.in_discount}
                              onChange={(e) =>
                                setProductForm((prev) => ({
                                  ...prev,
                                  in_discount: Number(e.target.value),
                                }))
                              }
                            >
                              <option value={0}>لا</option>
                              <option value={1}>نعم</option>
                            </select>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">سعر الخصم</label>
                            <input
                              type="number"
                              className="form-control rounded-pill"
                              value={productForm.discount_price}
                              onChange={(e) =>
                                setProductForm((prev) => ({
                                  ...prev,
                                  discount_price: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>

                        <div className="mb-3">
                          <label className="form-label">صور المنتج</label>
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            multiple
                            onChange={(e) => setProductFiles(e.target.files || [])}
                          />
                        </div>

                        {productForm.images?.length ? (
                          <div className="d-flex flex-wrap gap-2 mb-3">
                            {productForm.images.map((img, index) => (
                              <img
                                key={`${img.product_photo}-${index}`}
                                src={resolveMediaUrl(img.product_photo, Logo)}
                                alt="preview"
                                className="admin-preview-thumb rounded-4"
                              />
                            ))}
                          </div>
                        ) : null}

                        <div className="form-check form-switch mb-4">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={productForm.is_active}
                            onChange={(e) =>
                              setProductForm((prev) => ({
                                ...prev,
                                is_active: e.target.checked,
                              }))
                            }
                            id="productActive"
                          />
                          <label className="form-check-label" htmlFor="productActive">
                            نشط
                          </label>
                        </div>

                        <button className="btn btn-dark rounded-pill w-100" type="submit" disabled={saving}>
                          {saving ? "جارٍ الحفظ..." : productForm.id ? "تحديث المنتج" : "إضافة المنتج"}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>

                <div className="col-lg-7">
                  <div className="table-responsive">
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th>الصورة</th>
                          <th>الاسم</th>
                          <th>التصنيف</th>
                          <th>السعر</th>
                          <th>الإجراء</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <img
                                src={resolveMediaUrl(item?.images?.[0]?.product_photo, Logo)}
                                alt={item.product_title_ar}
                                className="admin-table-thumb rounded-4"
                              />
                            </td>
                            <td>
                              <div>{item.product_title_ar}</div>
                              <small className="text-body-secondary">{item.product_code}</small>
                            </td>
                            <td>{activeCategoryName(item.categories_id)}</td>
                            <td>{item.product_price} د.ع</td>
                            <td>
                              <div className="d-flex gap-2">
                                <button className="btn btn-sm btn-outline-dark rounded-pill" onClick={() => editProduct(item)}>
                                  تعديل
                                </button>
                                <button className="btn btn-sm btn-outline-danger rounded-pill" onClick={() => deleteProductItem(item.id)}>
                                  حذف
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {!products.length ? (
                          <tr>
                            <td colSpan={5} className="text-center text-body-secondary">
                              لا توجد منتجات
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === "orders" ? (
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>الطلب</th>
                      <th>العميل</th>
                      <th>المنتجات</th>
                      <th>الإجمالي</th>
                      <th>التاريخ</th>
                      <th>الحالة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="fw-semibold">{item.order_code}</div>
                          <small className="text-body-secondary">{item.phone || "—"}</small>
                        </td>
                        <td>
                          <div>{item.customer_name || "—"}</div>
                          <small className="text-body-secondary">{item.city || "—"}</small>
                        </td>
                        <td>
                          {item.items?.length ? (
                            <div className="small">
                              {item.items.slice(0, 2).map((product, index) => (
                                <div key={`${item.id}-${index}`}>
                                  {product.product_title_ar || product.name || "منتج"} × {product.piece || 0}
                                </div>
                              ))}
                              {item.items.length > 2 ? "..." : null}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{item.total_price} د.ع</td>
                        <td>{formatDateTime(item.createdAt)}</td>
                        <td style={{ minWidth: 180 }}>
                          <select
                            className="form-select rounded-pill"
                            value={item.status}
                            onChange={(e) => updateOrderStatus(item.id, e.target.value)}
                          >
                            {orderStatuses.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                    {!orders.length ? (
                      <tr>
                        <td colSpan={6} className="text-center text-body-secondary">
                          لا توجد طلبات
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            ) : null}

            {activeTab === "contacts" ? (
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>الاسم</th>
                      <th>التواصل</th>
                      <th>الموضوع</th>
                      <th>الرسالة</th>
                      <th>التاريخ</th>
                      <th>الحالة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((item) => (
                      <tr key={item.id}>
                        <td>{item.name || "—"}</td>
                        <td>
                          <div>{item.email || "—"}</div>
                          <small className="text-body-secondary">{item.phone || "—"}</small>
                        </td>
                        <td>{item.subject || "—"}</td>
                        <td style={{ minWidth: 250 }}>{item.message || "—"}</td>
                        <td>{formatDateTime(item.createdAt)}</td>
                        <td style={{ minWidth: 180 }}>
                          <select
                            className="form-select rounded-pill"
                            value={item.status}
                            onChange={(e) => updateContactStatus(item.id, e.target.value)}
                          >
                            {contactStatuses.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                    {!contacts.length ? (
                      <tr>
                        <td colSpan={6} className="text-center text-body-secondary">
                          لا توجد رسائل
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </main>
  );
}

export default AdminDashboard;